/**
 * Install prompt capture (Phase 16A, design §4).
 *
 * Chromium fires `beforeinstallprompt` once, early, and only for an installable app (manifest +
 * service worker). `captureInstallPrompt()` runs from src/index.tsx before React mounts so the
 * event is never missed; the login screen's Install button then calls `promptInstall()`.
 */

/** The non-standard Chromium event (not in lib.dom). */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

type Listener = () => void;

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
let captured = false;
const listeners = new Set<Listener>();

const notify = () => listeners.forEach((l) => l());

/** Register the window listeners once. Safe to call more than once. */
export function captureInstallPrompt(): void {
  if (captured || typeof window === 'undefined') return;
  captured = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault(); // keep it for the Install button instead of the browser mini-bar
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    notify();
  });
}

export const InstallPrompt = {
  /** A native install dialog can be shown right now. */
  canPrompt: (): boolean => deferred !== null,
  /** The app was installed during this page's life (appinstalled fired). */
  wasInstalled: (): boolean => installed,

  /** Show the native dialog. Resolves true when the user accepted. */
  async promptInstall(): Promise<boolean> {
    const event = deferred;
    if (!event) return false;
    deferred = null; // a prompt event can be used once
    await event.prompt();
    const choice = await event.userChoice;
    notify();
    return choice.outcome === 'accepted';
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },

  /** Test hook: reset module state. */
  resetForTests(): void {
    deferred = null;
    installed = false;
    captured = false;
    listeners.clear();
  },
};

export default InstallPrompt;
